import PropTypes from 'prop-types';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// @mui
import {
  Box,
  List,
  Badge,
  Button,
  Avatar,
  Tooltip,
  Divider,
  Popover,
  Typography,
  IconButton,
  ListItemText,
  ListSubheader,
  ListItemAvatar,
  ListItemButton,
} from '@mui/material';
// utils
import { fToNow } from '../../../utils/formatTime';
// components
import Iconify from '../../../components/iconify';
import Scrollbar from '../../../components/scrollbar';
// service
import { constant } from '../../../constant';
import { connectSocket } from '../../../service/socket';

// ----------------------------------------------------------------------

const POLL_MS = 30_000;

export default function NotificationsPopover() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(null);
  const pollRef = useRef(null);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${constant.baseUrl}notifications?limit=20`, {
        headers: { Authorization: token },
      });
      if (res?.data) {
        setNotifications(res.data.items || []);
        setUnreadCount(res.data.unreadCount || 0);
      }
    } catch (err) {
      console.error('notifications fetch failed', err);
    }
  };

  const postQuiet = async (path) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    await axios.post(`${constant.baseUrl}${path}`, {}, {
      headers: { Authorization: token },
    });
  };

  useEffect(() => {
    fetchNotifications();
    pollRef.current = setInterval(fetchNotifications, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    const socket = connectSocket();
    if (!socket) return undefined;
    const refetch = () => fetchNotifications();
    socket.on('leadAssigned', refetch);
    socket.on('sharkTankLead', refetch);
    socket.on('notification', refetch);
    return () => {
      socket.off('leadAssigned', refetch);
      socket.off('sharkTankLead', refetch);
      socket.off('notification', refetch);
    };
  }, []);

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleMarkAllAsRead = async () => {
    const now = new Date().toISOString();
    setNotifications((prev) => prev.map((n) => ({ ...n, readAt: n.readAt || now })));
    setUnreadCount(0);
    try {
      await postQuiet('notifications/mark-all-read');
    } catch (err) {
      console.error('mark-all-read failed', err);
      fetchNotifications();
    }
  };

  const handleClickItem = async (notification) => {
    if (!notification.readAt) {
      const now = new Date().toISOString();
      setNotifications((prev) =>
        prev.map((n) => (n._id === notification._id ? { ...n, readAt: now } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
      try {
        await postQuiet(`notifications/${notification._id}/read`);
      } catch (err) {
        console.error('mark-read failed', err);
      }
    }
    handleClose();
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const newOnes = notifications.filter((n) => !n.readAt).slice(0, 5);
  const older = notifications.filter((n) => n.readAt).slice(0, 5);

  return (
    <>
      <IconButton color={open ? 'primary' : 'default'} onClick={handleOpen} sx={{ width: 40, height: 40 }}>
        <Badge badgeContent={unreadCount} color="error">
          <Iconify icon="eva:bell-fill" />
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1.5,
            ml: 0.75,
            width: 360,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">Notifications</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              You have {unreadCount} unread {unreadCount === 1 ? 'message' : 'messages'}
            </Typography>
          </Box>

          {unreadCount > 0 && (
            <Tooltip title="Mark all as read">
              <IconButton color="primary" onClick={handleMarkAllAsRead}>
                <Iconify icon="eva:done-all-fill" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Scrollbar sx={{ height: { xs: 340, sm: 'auto' }, maxHeight: 480 }}>
          {newOnes.length > 0 && (
            <List
              disablePadding
              subheader={
                <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline' }}>
                  New
                </ListSubheader>
              }
            >
              {newOnes.map((notification) => (
                <NotificationItem
                  key={notification._id}
                  notification={notification}
                  onClick={handleClickItem}
                />
              ))}
            </List>
          )}

          {older.length > 0 && (
            <List
              disablePadding
              subheader={
                <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline' }}>
                  Before that
                </ListSubheader>
              }
            >
              {older.map((notification) => (
                <NotificationItem
                  key={notification._id}
                  notification={notification}
                  onClick={handleClickItem}
                />
              ))}
            </List>
          )}

          {notifications.length === 0 && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                No notifications yet
              </Typography>
            </Box>
          )}
        </Scrollbar>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Box sx={{ p: 1 }}>
          <Button fullWidth disableRipple onClick={fetchNotifications}>
            Refresh
          </Button>
        </Box>
      </Popover>
    </>
  );
}

// ----------------------------------------------------------------------

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    _id: PropTypes.string,
    type: PropTypes.string,
    title: PropTypes.string,
    body: PropTypes.string,
    link: PropTypes.string,
    readAt: PropTypes.any,
    createdOn: PropTypes.any,
  }),
  onClick: PropTypes.func,
};

function NotificationItem({ notification, onClick }) {
  const isUnread = !notification.readAt;
  const { avatar, title } = renderContent(notification);
  const created = notification.createdOn ? new Date(notification.createdOn) : new Date();

  return (
    <ListItemButton
      onClick={() => onClick && onClick(notification)}
      sx={{
        py: 1.5,
        px: 2.5,
        mt: '1px',
        ...(isUnread && {
          bgcolor: 'action.selected',
        }),
      }}
    >
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: 'background.neutral' }}>{avatar}</Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={title}
        secondary={
          <Typography
            variant="caption"
            sx={{
              mt: 0.5,
              display: 'flex',
              alignItems: 'center',
              color: 'text.disabled',
            }}
          >
            <Iconify icon="eva:clock-outline" sx={{ mr: 0.5, width: 16, height: 16 }} />
            {fToNow(created)}
          </Typography>
        }
      />
    </ListItemButton>
  );
}

// ----------------------------------------------------------------------

function renderContent(notification) {
  const title = (
    <Typography variant="subtitle2">
      {notification.title}
      {notification.body && (
        <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
          &nbsp; {notification.body}
        </Typography>
      )}
    </Typography>
  );

  if (
    notification.type === 'bankrate_lead_assigned' ||
    notification.type === 'bankrate_shark_tank_claim'
  ) {
    return {
      avatar: <Iconify icon="eva:person-add-fill" width={24} height={24} />,
      title,
    };
  }
  if (notification.type === 'bankrate_shark_tank') {
    return {
      avatar: <Iconify icon="eva:flash-fill" width={24} height={24} />,
      title,
    };
  }
  return {
    avatar: <Iconify icon="eva:bell-fill" width={24} height={24} />,
    title,
  };
}
